import { defineStore } from 'pinia';

import { Config } from '@/config';
import { Constants } from '@/constants';
import { UnauthorizedError } from '@/errors';
import { getSupportedLocales, setLocale } from '@/plugins/i18n';
import accountsService from '@/services/accounts.service';
import apiService from '@/services/api.service';
import type { Locales, UserDto } from '@/services/types/api.types';
import { useCommunityStore, useUsersStore } from '@/stores';

interface State {
  currentCommunity: number | null;
  role: number;
  user: UserDto | null;
}

const fetchUser = async (): Promise<UserDto | null> => {
  if (localStorage.getItem(accessTokenKey)) {
    const response = await apiService.get<{ user: UserDto }>(`${Config.api.v2}/user`);
    if (response.success) {
      useUsersStore().currentUser = response.data.user;
      return response.data.user;
    }
  }
  return null;
};

const accessTokenKey = 'accessToken';

export const useAuthStore = defineStore({
  id: 'auth',
  state: (): State => ({
    currentCommunity: null,
    role: Constants.User.Role.VIEWER,
    user: null,
  }),
  getters: {},
  actions: {
    async init() {
      this.user = await fetchUser();
      if (this.user) {
        this.role = this.user.role;
        if (getSupportedLocales().includes(this.user.language_code)) setLocale(this.user.language_code);
      }
    },
    // The account language also decides the language of every email the admin receives.
    async chooseLocale(locale: Locales) {
      setLocale(locale);
      if (!this.user || this.user.language_code === locale) return;
      try {
        await apiService.put(`${Config.api.v2}/user`, { language_code: locale });
        this.user.language_code = locale;
      } catch {
        // Best effort: apiService already logged it, and the interface language has changed either way.
      }
    },
    async login(token: string) {
      localStorage.setItem(accessTokenKey, token);
      await this.init();
      const roles = [Constants.User.Role.SUPERADMIN, Constants.User.Role.ADMIN, Constants.User.Role.EDITOR];
      if (!roles.includes(this.role)) throw new UnauthorizedError();
    },
    logout() {
      localStorage.removeItem(accessTokenKey);
      let redirectUri = `${window.location.protocol}//${window.location.host}/`;
      const communityStore = useCommunityStore();
      const slug = communityStore.getCommunitySlug();
      if (slug) redirectUri += `login/${slug}`;
      const urlSearchParams = new URLSearchParams();
      urlSearchParams.set('redirectUri', redirectUri);
      communityStore.clearCommunitySlug();
      this.currentCommunity = null;
      this.role = Constants.User.Role.VIEWER;
      this.user = null;
      window.location.href = `${Config.accounts.url}/logout?${urlSearchParams.toString()}`;
    },
    async refreshToken(communityId: number, forceReload: boolean, accessReference?: string) {
      const token = (await accountsService.login(communityId, accessReference)).token;
      localStorage.setItem(accessTokenKey, token);
      if (!forceReload) {
        this.currentCommunity = communityId;
        await this.init();
      }
    },
  },
});
